import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import WalletModal from '../components/WalletModal'
import SubscriptionModal from '../components/SubscriptionModal'
import WebMessagesModal from '../components/WebMessagesModal'
import { useLocation, useNavigate } from 'react-router-dom'
import PaymentSuccessModal from '../components/PaymentSuccessModal'
import UserNetworkModal from '../components/UserNetworkModal'
import AppFeaturesModalHome from '../components/AppFeaturesModalHome'
import HowWorks from '../components/HowWorks'
import PersonalDataModal from '../components/PersonalDataModal'
import VehicleBox from '../components/VehicleBox'
import ServiceBox from '../components/ServiceBox'
import ReceiptsBox from '../components/ReceiptsBox'
import TripsBox from '../components/TripsBox'
import { useQuery } from '@tanstack/react-query'
import AdvertisementModal from '../components/AdvertisementModal'
import StatsModal from '../components/StatsModal'
import QRModal from '../components/QRModal'
import NotificationPopupModal from '../components/NotificationPopupModal'
import { showFloatingPromoBannerFromClickable } from '../utils/promoBannerEvents'
import MFAPromptModal from '../components/MFAPromptModal'
import MFAModal from '../components/MFAModal'
import AdminMenu from '../components/AdminMenu'
import AdminMembershipTypes from './AdminMembershipTypes'
import AdminMlmBonusLevels from './AdminMlmBonusLevels'
import AdminInsurance from './AdminInsurance'
import AdminPromoBanners from './AdminPromoBanners'
import AdminCountries from './AdminCountries'
import AdminUsers from './AdminUsers'
import AdminReceipts from './AdminReceipts'
import AdminAuditLogs from './AdminAuditLogs'
import AdminWallet from './AdminWallet'
import AdminNetworkViz from './AdminNetworkViz'
import AdminLedger from './admin/ledger/AdminLedger'
import PaymentAllocationsAdmin from './PaymentAllocationsAdmin'
import AdminCashbackLab from './AdminCashbackLab'
import AdminSystemParameters from './AdminSystemParameters'
import AdminSubscriptions from './AdminSubscriptions'

const QUICKBOX_MESSAGES_REFRESH_EVENT = 'quickbox-messages-refresh'

const icons = {
  triplog: require('../assets/images/icons/triplog.png'),
  vehicles: require('../assets/images/icons/car.png'),
  services: require('../assets/images/icons/service.png'),
  receipts: require('../assets/images/icons/fuel.png'),
  accident: require('../assets/images/icons/accident.png'),
  personal: require('../assets/images/icons/registration.png'),
  qr: require('../assets/images/icons/qr_code.png'),

  wallet: require('../assets/images/icons/wallet.png'),
  stats: require('../assets/images/icons/stats.png'),
  subscription: require('../assets/images/icons/subscription.png'),
  how: require('../assets/images/icons/how_works.png'),
  benefits: require('../assets/images/icons/app_benefits.png'),
  network: require('../assets/images/icons/network.png'),
  messages: require('../assets/images/icons/messages.png'),
  bell: require('../assets/images/icons/bell.png'),
  adds: require('../assets/images/icons/adds.png'),
}

const Home = () => {
  const { t } = useTranslation()
  const { user, refreshUser } = useAuth()
  const [showMFAPrompt, setShowMFAPrompt] = useState(false)
  const [showMFAModal, setShowMFAModal] = useState(false)
  const [walletBalance, setWalletBalance] = useState(0)
  const [walletEntries, setWalletEntries] = useState([])
  const [membershipName, setMembershipName] = useState(null)
  const [viewport, setViewport] = useState(window.innerWidth)
  const [activeModal, setActiveModal] = useState(null)
  const [plans, setPlans] = useState([])
  const [webMessages, setWebMessages] = useState([])
  const [paymentSuccessOpen, setPaymentSuccessOpen] = useState(false)
  const [centerView, setCenterView] = useState(null)
  const [showAdminButtons, setShowAdminButtons] = useState(
  () => sessionStorage.getItem('admin-home-mode') === 'true'
)

  const [networkCount, setNetworkCount] = useState(0)
  const [levelCounts, setLevelCounts] = useState({ l1: 0, l2: 0, l3: 0 })
  const [levelBonuses, setLevelBonuses] = useState({ l1: 0, l2: 0, l3: 0 })
  const [vehiclesById, setVehiclesById] = useState({})

const [hoverBtn, setHoverBtn] = useState(null)
const [pressedBtn, setPressedBtn] = useState(null)

const [lastTrips, setLastTrips] = useState([])
const [lastReceipts, setLastReceipts] = useState([])
const [lastServices, setLastServices] = useState([]) 
const [currentMonthFuelAmount, setCurrentMonthFuelAmount] = useState(0)
const [msgAnim, setMsgAnim] = useState(false)
const [prevMsgCount, setPrevMsgCount] = useState(() => webMessages.length)
const [showFullscreenHint, setShowFullscreenHint] = useState(false)
const [tooltipTimer, setTooltipTimer] = useState(null)
const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 })
const [popupNotification, setPopupNotification] = useState(null)
const [popupOpen, setPopupOpen] = useState(false)
const [showReferralHint, setShowReferralHint] = useState(false)
const [referralTooltipTimer, setReferralTooltipTimer] = useState(null)
const [referralCursorPos, setReferralCursorPos] = useState({ x: 0, y: 0 })
const knownNotificationIdsRef = useRef(new Set())

  const location = useLocation()
  const navigate = useNavigate()

useEffect(() => {
  const handleHomeResetCenterView = () => {
    setCenterView(null)
    setActiveModal(null)
  }

  window.addEventListener('home-reset-center-view', handleHomeResetCenterView)

  return () => {
    window.removeEventListener('home-reset-center-view', handleHomeResetCenterView)
  }
}, [])

useEffect(() => {
const handleToggleAdminButtons = (event) => {
  if (user?.role !== 'admin') return

  const nextAdminMode = event.detail === true

  sessionStorage.setItem(
    'admin-home-mode',
    String(nextAdminMode)
  )

  setShowAdminButtons(nextAdminMode)
  setCenterView(null)
  setActiveModal(null)
}

  window.addEventListener(
    'toggle-admin-home-buttons',
    handleToggleAdminButtons
  )

  return () => {
    window.removeEventListener(
      'toggle-admin-home-buttons',
      handleToggleAdminButtons
    )
  }
}, [user?.role])

  useEffect(() => {
    const onResize = () => setViewport(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

useEffect(() => {
  const handleEsc = (e) => {
    if (e.key !== 'Escape') return

    if (activeModal === 'QR') {
      setActiveModal(null)
    }
  }

  window.addEventListener('keydown', handleEsc)
  return () => window.removeEventListener('keydown', handleEsc)
}, [activeModal])

const { data: walletData } = useQuery({
  queryKey: ['wallet-home', user?.id],
  enabled: !!user?.id,
  staleTime: 5 * 60 * 1000,
  queryFn: async () => {
    const res = await fetchWithAuth(`/wallet/${user.id}/entries`)
    if (!res.ok) return { entries: [], balance: 0 }

    const entries = await res.json()

    const sumCents = Array.isArray(entries)
      ? entries.reduce((s, e) => s + Math.round(Number(e?.amount || 0) * 100), 0)
      : 0

    return { entries, balance: sumCents / 100 }
  }
})

useEffect(() => {
  if (!walletData) return
  setWalletEntries(walletData.entries)
  setWalletBalance(walletData.balance)
}, [walletData])

const { data: membershipTypes } = useQuery({
  queryKey: ['membership-types'],
  staleTime: 5 * 60 * 1000,
  queryFn: async () => {
    const res = await fetchWithAuth('/membership/types')
    if (!res.ok) return []
    return await res.json()
  }
})

useEffect(() => {
  if (!membershipTypes) return

  setPlans(membershipTypes)

  if (!user?.membership_type_id) {
    setMembershipName(null)
    return
  }

  const found = membershipTypes.find(
    t => Number(t.id) === Number(user.membership_type_id)
  )

  setMembershipName(found?.type || null)
}, [membershipTypes, user?.membership_type_id])

const currentMembership = Array.isArray(membershipTypes)
  ? membershipTypes.find(mt => Number(mt.id) === Number(user?.membership_type_id))
  : null

const membershipMaxAmount = Number(currentMembership?.max_amount || 0)
const membershipFuelType = currentMembership?.is_electric ? 'electric' : 'fuel'

const { data: notificationsData, refetch: refetchNotifications } = useQuery({
  queryKey: ['notifications-home', user?.id],
  enabled: !!user?.id,
  staleTime: 5 * 60 * 1000,
  queryFn: async () => {
    const res = await fetchWithAuth('/notifications/web')
    if (!res.ok) return []
    const data = await res.json()
    return Array.isArray(data) ? data : []
  }
})

useEffect(() => {
  if (!notificationsData) return

  setWebMessages(notificationsData)

  const knownSet = knownNotificationIdsRef.current
  const newItems = notificationsData.filter((item) => !knownSet.has(String(item.id)))

  if (knownSet.size > 0 && newItems.length > 0) {
    const latest = [...newItems].sort((a, b) => {
      const aTime = a?.created_at ? new Date(a.created_at).getTime() : 0
      const bTime = b?.created_at ? new Date(b.created_at).getTime() : 0

      if (aTime !== bTime) return bTime - aTime
      return Number(b?.id || 0) - Number(a?.id || 0)
    })[0]

    if (latest) {
      setPopupNotification(latest)
      setPopupOpen(true)
    }
  }

  knownNotificationIdsRef.current = new Set(notificationsData.map((item) => String(item.id)))
}, [notificationsData])

useEffect(() => {
  if (prevMsgCount !== 0 && webMessages.length > prevMsgCount) {

    setMsgAnim(false)

    setTimeout(() => {

      setMsgAnim(true)

      const t = setTimeout(() => {
        setMsgAnim(false)
      }, 3000)

      return () => clearTimeout(t)

    }, 20)
  }

  setPrevMsgCount(webMessages.length)

}, [webMessages.length, prevMsgCount])

useEffect(() => {
  const handleMessagesRefresh = () => {
    refetchNotifications()
  }

  window.addEventListener(QUICKBOX_MESSAGES_REFRESH_EVENT, handleMessagesRefresh)
  return () => {
    window.removeEventListener(QUICKBOX_MESSAGES_REFRESH_EVENT, handleMessagesRefresh)
  }
}, [refetchNotifications])

const unreadWebMessagesCount = webMessages.filter(
  (msg) => msg?.statusWeb !== 'read'
).length

const now = new Date()
const cashbackMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`

const { data: cbSummary } = useQuery({
  queryKey: ['cashback-home-base', user?.id, cashbackMonthKey],
  enabled: !!user?.id,
  staleTime: 5 * 60 * 1000,
  queryFn: async () => {
    const res = await fetchWithAuth(
      `/cashback/${user.id}?month=${cashbackMonthKey}`
    )

    if (!res.ok) {
      return { base: 0, l1: 0, l2: 0, l3: 0, total: 0 }
    }

    return await res.json()
  }
})

const cbBase = Number(cbSummary?.base || 0)
const { data: networkData } = useQuery({
  queryKey: ['network-home', user?.id],
  enabled: !!user?.id,
  staleTime: 5 * 60 * 1000,
  queryFn: async () => {
    const res = await fetchWithAuth(`/referral/network/${user.id}`)
    if (!res.ok) return { count: 0, l1: 0, l2: 0, l3: 0 }

    const data = await res.json()

    return {
      count: data.length,
      l1: data.filter(n => n.generation === 1).length,
      l2: data.filter(n => n.generation === 2).length,
      l3: data.filter(n => n.generation === 3).length
    }
  }
})

useEffect(() => {
  if (!networkData) return
  setNetworkCount(networkData.count)
  setLevelCounts({
    l1: networkData.l1,
    l2: networkData.l2,
    l3: networkData.l3
  })
}, [networkData])

const { data: cashbackData } = useQuery({
  queryKey: ['cashback-home-levels', user?.id, cashbackMonthKey],
  enabled: !!user?.id,
  staleTime: 5 * 60 * 1000,
  queryFn: async () => {
    const res = await fetchWithAuth(`/cashback/${user.id}?month=${cashbackMonthKey}`)
    if (!res.ok) return { l1: 0, l2: 0, l3: 0 }
    const data = await res.json()
    return {
      l1: Number(data?.l1 || 0),
      l2: Number(data?.l2 || 0),
      l3: Number(data?.l3 || 0)
    }
  }
})

useEffect(() => {
  if (!cashbackData) return
  setLevelBonuses(cashbackData)
}, [cashbackData])

const { data: vehiclesData } = useQuery({
  queryKey: ['vehicles-home', user?.id],
  enabled: !!user?.id,
  staleTime: 5 * 60 * 1000,
  queryFn: async () => {
    const res = await fetchWithAuth(`/users/${user.id}/vehicles`)
    if (!res.ok) return {}

    const data = await res.json()
    if (!Array.isArray(data)) return {}

    return data.reduce((acc, v) => {
      acc[String(v.id)] = v
      return acc
    }, {})
  }
})

useEffect(() => {
  if (!vehiclesData) return
  setVehiclesById(vehiclesData)
}, [vehiclesData])

const { data: homeCenterData } = useQuery({
  queryKey: ['home-center-boxes', user?.id],
  enabled: !!user?.id,
  staleTime: 5 * 60 * 1000,
  queryFn: async () => {
    const [tripsRes, receiptsRes, servicesRes] = await Promise.all([
      fetchWithAuth('/triplog/trips'),
      fetchWithAuth(`/users/${user.id}/receipts`),
      fetchWithAuth('/triplog/service-costs')
    ])

    const tripsData = tripsRes.ok ? await tripsRes.json() : []
    const receiptsData = receiptsRes.ok ? await receiptsRes.json() : []
    const servicesData = servicesRes.ok ? await servicesRes.json() : []

    const sortByDateDesc = (items) =>
      Array.isArray(items)
        ? [...items].sort((a, b) => new Date(b?.date || 0) - new Date(a?.date || 0))
        : []

    return {
      trips: sortByDateDesc(tripsData).slice(0, 4),
      receipts: sortByDateDesc(receiptsData).slice(0, 4),
      allReceipts: Array.isArray(receiptsData) ? receiptsData : [],
      services: sortByDateDesc(servicesData).slice(0, 4)
    }
  }
})

useEffect(() => {
  if (!homeCenterData) return

  setLastTrips(homeCenterData.trips)
  setLastReceipts(homeCenterData.receipts)
  setLastServices(homeCenterData.services)

  const now = new Date()
  const month = now.getMonth()
  const year = now.getFullYear()

const currentMonthSum = (homeCenterData.allReceipts || []).reduce((sum, r) => {
  const d = new Date(r?.date)

  if (Number.isNaN(d.getTime())) return sum
  if (d.getMonth() !== month || d.getFullYear() !== year) return sum
  if (r?.manual === true) return sum

  const receiptFuelType = String(r?.fuel_type || 'fuel').toLowerCase()

  if (receiptFuelType !== membershipFuelType) return sum

  return sum + Number(r?.units || r?.liters || 0)
}, 0)

setCurrentMonthFuelAmount(currentMonthSum)
}, [homeCenterData, membershipFuelType])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const status = params.get('status')

    const checkoutStartedAt = Number(sessionStorage.getItem('stripe_checkout_started_at') || 0)
    const isFreshCheckoutReturn =
      checkoutStartedAt > 0 &&
      (Date.now() - checkoutStartedAt) < 30 * 60 * 1000

    if (!status && !isFreshCheckoutReturn) return

if (status === 'success') {
  setPaymentSuccessOpen(true)
  setActiveModal('SUBSCRIPTION')

  let refreshCount = 0

  const interval = setInterval(() => {
    window.dispatchEvent(
      new Event(QUICKBOX_MESSAGES_REFRESH_EVENT)
    )

    refreshCount++

    if (refreshCount >= 10) {
      clearInterval(interval)
    }
  }, 2000)
} else if (status === 'cancel' || isFreshCheckoutReturn) {
  setPaymentSuccessOpen(false)
  setActiveModal('SUBSCRIPTION')
}

    sessionStorage.removeItem('stripe_checkout_started_at')
    navigate(location.pathname, { replace: true })
  }, [location.search, location.pathname, navigate])

useEffect(() => {
  if (!user) return

  if (user.mfa_enabled) return

  if (user.mfa_prompt_shown) return

  let cancelled = false

  const showAndMarkMFAPrompt = async () => {
    setShowMFAPrompt(true)

    const res = await fetchWithAuth('/auth/mfa/prompt-shown', { method: 'POST' })
    if (cancelled || !res.ok) return

    await refreshUser(true)
  }

  showAndMarkMFAPrompt()

  return () => {
    cancelled = true
  }
}, [user, refreshUser])
  let layout = {}

  if (viewport >= 1920) {
    layout = {
      columns: 'auto minmax(0,1fr) auto',
      areas: `"left center right"`,
      boxWidth: 205,
      boxHeight: 43,
      fontSize: 15,
      quickTitleFont: 14,
      quickLabelFont: 18,
      quickValueFont: 26,
      quickPadding: 12.5,  
      iconSize: 30,
      bottomCols: 'repeat(3, 1fr)',
      vehicleHeaderFontSize: 13,
      vehicleRowFontSize: 13,
      gap: 12
    }
  } 
  else if (viewport >= 1366) {
    layout = {
      columns: 'auto minmax(0,1fr) auto',
      areas: `"left center right"`,
      boxWidth: 190,
      boxHeight: 35,
      fontSize: 13,
      quickTitleFont: 13,
      quickLabelFont: 16,
      quickValueFont: 24,
      quickPadding: 13.7,  
      iconSize: 28,
      bottomCols: 'repeat(3, 1fr)',
      vehicleRowFontSize: 12,
      vehicleHeaderFontSize: 12,
      gap: 12,
      adminMenuGap: 4.5
    }
  } 
  else {
    layout = {
      columns: 'auto minmax(0,1fr) auto',
      areas: `"left center right"`,
      boxWidth: viewport <= 768 ? 100 : 200,
      boxHeight: 40,
      fontSize: viewport <= 768 ? 10 : 13,
      quickTitleFont: 12,
      quickLabelFont: 15,
      quickValueFont: 22,
      quickPadding: 12,
      iconSize: viewport <= 768 ? 20 : 30,
      bottomCols: '1fr',
      vehicleHeaderFontSize: 12,
      gap: 12
    }
  }

  const boxes = [
    { key: 'TRIP_LIST', icon: icons.triplog },
    { key: 'RECEIPT_LIST', icon: icons.receipts },
    { key: 'SERVICE_LIST', icon: icons.services },
    { key: 'VEHICLE_LIST', icon: icons.vehicles },
    { key: 'STATISTICS', icon: icons.stats },
    { key: 'NETWORK_OVERVIEW', icon: icons.network },
    { key: 'WALLET', icon: icons.wallet },
    { key: 'SUBSCRIPTION', icon: icons.subscription },
    { key: 'MESSAGES', icon: icons.messages },
    { key: 'USER_PROFILE', icon: icons.personal },
    { key: 'HOW_IT_WORKS', icon: icons.how },
    { key: 'APP_BENEFITS', icon: icons.benefits },
    { key: 'ADDS', icon: icons.adds }
  ]

  return (
    <div style={wrapper} onClickCapture={showFloatingPromoBannerFromClickable}>
	<div
	  style={{
	    ...contentArea,
	    gridTemplateColumns:
	      showAdminButtons && user?.role === 'admin'
 	       ? 'auto minmax(0,1fr)'
 	       : layout.columns,
	    gridTemplateAreas:
	      showAdminButtons && user?.role === 'admin'
	        ? '"left center"'
	        : layout.areas,
	    gap: layout.gap
	  }}
	>
<div style={{ ...leftColumn, gridArea: 'left' }}>
  {showAdminButtons && user?.role === 'admin' ? (
    <AdminMenu
      layout={layout}
      setCenterView={setCenterView}
    />
  ) : (
    <div style={boxList}>
      {boxes.map((item, index) => (
        <div
          key={index}
          data-promo-clickable="true"
          onClick={() => {
            if (item.key === 'STATISTICS') {
              setActiveModal('STATS_BUTTON')
            } else if (
              [
                'WALLET',
                'SUBSCRIPTION',
                'MESSAGES',
                'NETWORK_OVERVIEW',
                'APP_BENEFITS',
                'HOW_IT_WORKS',
                'USER_PROFILE',
                'ADDS',
              ].includes(item.key)
            ) {
              setActiveModal(item.key)
            } else {
              setCenterView(item.key)
            }
          }}
          style={{
            ...box,
            width: layout.boxWidth,
            height: viewport <= 768 ? 32 : layout.boxHeight,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background =
              'rgba(16,124,107,0.30)'

            e.currentTarget.style.boxShadow =
              '0 6px 16px rgba(0,0,0,0.35)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background =
              'rgba(16,124,107,0.06)'

            e.currentTarget.style.boxShadow =
              '0 4px 12px rgba(0,0,0,0.25)'

            e.currentTarget.style.transform = 'scale(1)'
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = 'scale(0.96)'

            e.currentTarget.style.boxShadow =
              '0 2px 6px rgba(0,0,0,0.2)'
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'scale(1)'

            e.currentTarget.style.boxShadow =
              '0 6px 16px rgba(0,0,0,0.35)'
          }}
        >
<div
  style={{
    ...boxContent,
    ...(viewport <= 768
      ? {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          width: '100%',
          boxSizing: 'border-box'
        }
      : {})
  }}
>
  <img
    src={item.icon}
    alt=""
    style={{
      ...getBoxIcon(layout),
      ...(viewport <= 768
        ? {
            flex: '0 0 auto'
          }
        : {})
    }}
  />

  <h3
    style={{
      ...boxTitle,
      fontSize: layout.fontSize,
      ...(viewport <= 768
        ? {
            flex: '0 1 auto',
            width: 'auto',
            minWidth: 0,
            maxWidth: 72,
            textAlign: 'center',
            margin: 0
          }
        : {})
    }}
  >
    {t(item.key === 'USER_PROFILE' && user?.account_type === 'company' ? 'COMPANY_PROFILE' : item.key)}
  </h3>
</div>
        </div>
      ))}
    </div>
  )}
</div>

{/* STRED */}
<div style={{ gridArea: 'center', width: '100%', minHeight: 0 }}>
{centerView ? (
  centerView === 'VEHICLE_LIST' ? (
    <VehicleBox
      user={user}
      t={t}
      layout={layout}
      viewport={viewport}
    />
  ) : centerView === 'SERVICE_LIST' ? (
    <ServiceBox
      user={user}
      t={t}
      layout={layout}
      viewport={viewport}
    />
  ) : centerView === 'RECEIPT_LIST' ? (
    <ReceiptsBox
      user={user}
      t={t}
      layout={layout}
      viewport={viewport}
    />
  ) : centerView === 'TRIP_LIST' ? (
    <TripsBox
      user={user}
      t={t}
      layout={layout}
      viewport={viewport}
    />
) : centerView === 'ADMIN_MEMBERSHIP_TYPES' ? (
  <AdminMembershipTypes />
) : centerView === 'ADMIN_MLM_BONUS_LEVELS' ? (
  <AdminMlmBonusLevels />
) : centerView === 'ADMIN_INSURANCE' ? (
  <AdminInsurance />
) : centerView === 'ADMIN_PROMO_BANNERS' ? (
  <AdminPromoBanners />
) : centerView === 'ADMIN_COUNTRIES' ? (
  <AdminCountries />
) : centerView === 'ADMIN_SYSTEM_PARAMETERS' ? (
  <AdminSystemParameters />
) : centerView === 'ADMIN_USERS' ? (
  <AdminUsers />
) : centerView === 'ADMIN_SUBSCRIPTIONS' ? (
  <AdminSubscriptions />
) : centerView === 'ADMIN_RECEIPTS' ? (
  <AdminReceipts />
) : centerView === 'ADMIN_AUDIT_LOGS' ? (
  <AdminAuditLogs />
) : centerView === 'ADMIN_WALLET' ? (
  <AdminWallet />
) : centerView === 'ADMIN_NETWORK_VIZ' ? (
  <AdminNetworkViz />
) : centerView === 'ADMIN_LEDGER' ? (
  <AdminLedger />
) : centerView === 'ADMIN_PAYMENT_ALLOCATIONS' ? (
  <PaymentAllocationsAdmin />
) : centerView === 'ADMIN_CASHBACK_LAB' ? (
  <AdminCashbackLab />
) : (
    <div style={bottomBox}>
      {t(centerView)}
    </div>
  )
) : (
  <div style={defaultCenterStack}>

<div
  style={{ ...lastTripBox, cursor: 'pointer' }}
  data-promo-clickable="true"
  onClick={() => {
  setCenterView('TRIP_LIST')

}}
>
  <h3 style={{ fontSize: 18, marginTop: 0, marginBottom: 10 }}>
    {t('TRIPLOG.TAB.LAST_TRIPS')}
  </h3>

  <div style={lastTripTableWrap}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: '#c8e1e1' }}>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.TRIPS.TABLE.date')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.TRIPS.TABLE.vehicleId')}</th>
          <th style={lastTripHeaderCell}>{t('vehicle_license_plate')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.TRIPS.TABLE.startTimeIso')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.TRIPS.TABLE.startPlace')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.TRIPS.TABLE.endTimeIso')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.TRIPS.TABLE.endPlace')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.TRIPS.TABLE.distanceKm')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.TRIPS.TABLE.driver')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.TRIPS.TABLE.purpose')}</th>
        </tr>
      </thead>

      <tbody>
        {lastTrips.slice(0, 4).map(trip => (
          <tr key={trip.id}>

            <td style={lastTripBodyCell}>
              {trip.date
                ? new Date(trip.date).toLocaleDateString('sk-SK')
                : '-'}
            </td>

            <td style={lastTripBodyCell}>
	{(() => {
	  const vehicle =
 	   trip.vehicle ||
  	  vehiclesById[String(trip.vehicleId)]

 	 return (
	    vehicle?.name ||
 	   vehicle?.model ||
  	  vehicle?.licensePlate ||
 	   vehicle?.spz ||
 	   '-'
	  )
	})()}
            </td>
<td style={lastTripBodyCell}>
{(() => {
  const vehicle =
    trip.vehicle ||
    vehiclesById[String(trip.vehicleId)]

  return (
    vehicle?.licensePlate ||
    vehicle?.spz ||
    '-'
  )
})()}
</td>
            <td style={lastTripBodyCell}>
              {trip.startTimeIso
                ? new Date(trip.startTimeIso).toLocaleTimeString('sk-SK', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                : '-'}
            </td>

            <td style={lastTripBodyCell}>
              {trip.startPlace || '-'}
            </td>

            <td style={lastTripBodyCell}>
              {trip.endTimeIso
                ? new Date(trip.endTimeIso).toLocaleTimeString('sk-SK', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                : '-'}
            </td>

            <td style={lastTripBodyCell}>
              {trip.endPlace || '-'}
            </td>

            <td style={lastTripBodyCell}>
              {trip.distanceKm ?? '-'}
            </td>

            <td style={lastTripBodyCell}>
              {trip.driver || '-'}
            </td>

            <td style={lastTripBodyCell}>
              {trip.purpose || '-'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>

<div
  style={{ ...lastTripBox, cursor: 'pointer' }}
  data-promo-clickable="true"
  onClick={() => {
  setCenterView('RECEIPT_LIST')

}}
>
  <h3 style={{ fontSize: 18, marginTop: 0, marginBottom: 10 }}>
    {t('TRIPLOG.TAB.LAST_RECEIPTS')}
  </h3>

  <div style={lastTripTableWrap}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: '#c8e1e1' }}>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.RECEIPTS.TABLE.DATE')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.RECEIPTS.TABLE.VEHICLE')}</th>
	  <th style={lastTripHeaderCell}>{t('vehicle_license_plate')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.RECEIPTS.TABLE.FUEL_NAME')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.RECEIPTS.TABLE.UNIT_PRICE')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.RECEIPTS.TABLE.UNITS')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.RECEIPTS.TABLE.UNIT')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.RECEIPTS.TABLE.TOTAL_PRICE')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.RECEIPTS.TABLE.CURRENCY')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.RECEIPTS.TABLE.VAT_RATE')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.RECEIPTS.TABLE.STORE_NAME')}</th>
        </tr>
      </thead>

      <tbody>
        {lastReceipts.slice(0, 4).map(r => (
          <tr key={r.id}>

            <td style={lastTripBodyCell}>
              {r.date
                ? new Date(r.date).toLocaleDateString('sk-SK')
                : '-'}
            </td>

            <td style={lastTripBodyCell}>
	{(() => {
	  const vehicle =
	    r.vehicle ||
 	   vehiclesById[String(r.vehicleId)]

	  return (
	    vehicle?.name ||
 	   vehicle?.model ||
 	   vehicle?.licensePlate ||
 	   vehicle?.spz ||
 	   '-'
 	 )
	})()}
            </td>
<td style={lastTripBodyCell}>
{(() => {
  const vehicle =
    r.vehicle ||
    vehiclesById[String(r.vehicleId)]

  return (
    vehicle?.licensePlate ||
    vehicle?.spz ||
    '-'
  )
})()}
</td>
            <td style={lastTripBodyCell}>{r.fuel_name || '-'}</td>
            <td style={lastTripBodyCell}>{r.unit_price ?? '-'}</td>
            <td style={lastTripBodyCell}>{r.units ?? '-'}</td>
            <td style={lastTripBodyCell}>{r.unit || '-'}</td>
            <td style={lastTripBodyCell}>{r.total_price ?? '-'}</td>
            <td style={lastTripBodyCell}>{r.currency || '-'}</td>
            <td style={lastTripBodyCell}>{r.vat_rate ?? '-'}</td>
            <td style={lastTripBodyCell}>{r.store_name || '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>

<div
  style={{ ...lastTripBox, cursor: 'pointer' }}
  data-promo-clickable="true"
  onClick={() => {
  setCenterView('SERVICE_LIST')

}}
>
  <h3 style={{ fontSize: 18, marginTop: 0, marginBottom: 10 }}>
    {t('TRIPLOG.TAB.LAST_SERVICES')}
  </h3>

  <div style={lastTripTableWrap}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: '#c8e1e1' }}>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.SERVICECOSTS.TABLE.date')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.SERVICECOSTS.TABLE.vehicleId')}</th>
	  <th style={lastTripHeaderCell}>{t('vehicle_license_plate')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.SERVICECOSTS.TABLE.serviceType')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.SERVICECOSTS.TABLE.kilometers')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.SERVICECOSTS.TABLE.totalCost')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.SERVICECOSTS.TABLE.workshopName')}</th>
          <th style={lastTripHeaderCell}>{t('TRIPLOG.SERVICECOSTS.TABLE.description')}</th>
        </tr>
      </thead>

      <tbody>
        {lastServices.slice(0, 4).map(s => (
          <tr key={s.id}>

            <td style={lastTripBodyCell}>
              {s.date
                ? new Date(s.date).toLocaleDateString('sk-SK')
                : '-'}
            </td>

            <td style={lastTripBodyCell}>
		{(() => {
 		 const vehicle =
 		   s.vehicle ||
 		   vehiclesById[String(s.vehicleId)]

		  return (
  		  vehicle?.name ||
  		  vehicle?.model ||
  		  vehicle?.licensePlate ||
 		   vehicle?.spz ||
 		   '-'
 		 )
		})()}
            </td>
<td style={lastTripBodyCell}>
{(() => {
  const vehicle =
    s.vehicle ||
    vehiclesById[String(s.vehicleId)]

  return (
    vehicle?.licensePlate ||
    vehicle?.spz ||
    '-'
  )
})()}
</td>
            <td style={lastTripBodyCell}>{s.serviceType || '-'}</td>
            <td style={lastTripBodyCell}>{s.kilometers ?? '-'}</td>
            <td style={lastTripBodyCell}>{s.totalCost ?? '-'}</td>
            <td style={lastTripBodyCell}>{s.workshopName || '-'}</td>
            <td style={lastTripBodyCell}>{s.note || s.description || '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>

  </div>
  )}
</div>

{!(showAdminButtons && user?.role === 'admin') && (
        <div style={{ ...rightColumn, gridArea: 'right' }}>
<div
  style={{
    ...userBox,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }}
>
  <div>
    <div style={userLabel}>{t('LOGGED_IN_AS')}</div>
    <div style={userName}>
      {user?.meno} {user?.priezvisko}
    </div>
  </div>

<div
  data-promo-clickable="true"
  onClick={() => setActiveModal('QR')}
  onMouseEnter={(e) => {
    const timer = setTimeout(() => {
      setShowReferralHint(true)
    }, 600)

    setReferralTooltipTimer(timer)
    setReferralCursorPos({ x: e.clientX, y: e.clientY })

    e.currentTarget.style.background = '#2da9a4'
    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.35)'
  }}
  onMouseMove={(e) => {
    setReferralCursorPos({ x: e.clientX, y: e.clientY })
  }}
  onMouseLeave={(e) => {
    if (referralTooltipTimer) clearTimeout(referralTooltipTimer)

    setShowReferralHint(false)

    e.currentTarget.style.background = '#ededed'
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.30)'
  }}
  style={{
    width: 55,
    height: 55,
    borderRadius: 8,
    border: '1.5px solid #107c6b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#ededed',
    boxShadow: '0 4px 8px rgba(0,0,0,0.30)',
    cursor: 'pointer'
  }}
  onMouseDown={(e) => {
    e.currentTarget.style.transform = 'scale(0.96)'
    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e) => {
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.35)'
  }}
>
  <img
    src={icons.qr}
    alt=""
    style={{ width: 38, height: 38 }}
  />
</div>
</div>

<div style={rightPanel}>
  <h3 style={{ marginTop: -8, marginBottom: 10 }}>
    {t('HOME_QUICK_OVERVIEW')}
  </h3>

  {/* WALLET */}
<div
  style={{
    ...quickBox(layout),
  paddingTop: 6,
  paddingBottom: 6,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }}
  data-promo-clickable="true"
  onClick={() => setActiveModal('WALLET')}
  onMouseEnter={(e) => {
    e.currentTarget.style.background = '#2da9a4'
    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.35)'
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = '#f5f5f5'
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.30)'
  }}
  onMouseDown={(e) => {
    e.currentTarget.style.transform = 'scale(0.96)'
    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e) => {
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.35)'
  }}
>
    <div>
      <div style={{ fontSize: layout.quickTitleFont }}>
        <strong>{t('stats.wallet')}</strong>
      </div>
      <div style={{ fontSize: layout.quickLabelFont, fontWeight: 700 }}>
        {t('CURRENT_BALANCE')}
      </div>
    </div>

   <div style={{ fontSize: layout.quickValueFont, fontWeight: 800 }}>
      {walletData ? `${walletBalance.toFixed(2)} €` : null}
    </div>
  </div>

  {/* SUBSCRIPTION */}
<div
style={{
  ...quickBox(layout),
  paddingTop: 4,
  paddingBottom: 4,
}}
  data-promo-clickable="true"
  onClick={() => setActiveModal('SUBSCRIPTION')}
  onMouseEnter={(e) => {
    e.currentTarget.style.background = '#2da9a4'
    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.35)'
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = '#f5f5f5'
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.30)'
  }}
  onMouseDown={(e) => {
    e.currentTarget.style.transform = 'scale(0.96)'
    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e) => {
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.35)'
  }}
>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <strong style={{ position: 'relative', top: -2 }}>
        {t('HOME_SUBSCRIPTION')}
      </strong>

      <div style={{ fontSize: layout.quickValueFont, fontWeight: 800 }}>
        {membershipName || '-'}
      </div>
    </div>

    <div style={{ marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
      <span>{t('VALID_TO') + ':'}</span>

      <span
        style={{
          fontSize: layout.quickLabelFont,
          color: user?.membership_valid_to &&
            new Date(user.membership_valid_to).setHours(23, 59, 59, 999) < Date.now()
              ? 'red'
              : undefined,
          textDecoration: user?.membership_valid_to &&
            new Date(user.membership_valid_to).setHours(23, 59, 59, 999) < Date.now()
              ? 'line-through'
              : 'none'
        }}
      >
        {user?.membership_valid_to
          ? new Date(user.membership_valid_to).toLocaleDateString('sk-SK')
          : '-'}
      </span>
    </div>

    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>{t('HOME_FUEL_USAGE')}:</span>
        <strong>
 	 {Math.min(currentMonthFuelAmount, membershipMaxAmount).toFixed(2)} / {membershipMaxAmount || 0} {membershipFuelType === 'electric' ? 'kWh' : 'l'}
	</strong>
      </div>
      <div style={{ marginTop: 4, width: '100%', height: 8, background: '#e5e5e5', borderRadius: 20, overflow: 'hidden' }}>
        <div style={{ width: `${membershipMaxAmount > 0 ? Math.min((currentMonthFuelAmount / membershipMaxAmount) * 100, 100) : 0}%`, height: '100%', background: '#107c6b' }} />
      </div>
      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
        <span>{t('HOME_ACTUAL_BONUS', { defaultValue: 'Bonus za čerpanie' })}:</span>
        <strong>{cbBase.toFixed(2)} €</strong>
      </div>
    </div>
  </div>

{/* NETWORK OVERVIEW */}
<div
  style={{
    ...quickBox(layout),
  paddingTop: 4,
  paddingBottom: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  }}
  data-promo-clickable="true"
  onClick={() => setActiveModal('NETWORK_OVERVIEW')}
  onMouseEnter={(e) => {
    e.currentTarget.style.background = '#2da9a4'
    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.35)'
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = '#f5f5f5'
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.30)'
  }}
  onMouseDown={(e) => {
    e.currentTarget.style.transform = 'scale(0.96)'
    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e) => {
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.35)'
  }}
>
  <strong style={{ fontSize: layout.quickLabelFont }}>
    {t('YOUR_NETWORK')}
  </strong>

  {/* Celkový počet + teplomer */}
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <div>
      {t('NETWORK_TOTAL_MEMBERS')}: <strong>{networkCount}</strong>
    </div>

    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div
        style={{
          width: 80,
          height: 8,
          background: '#e5e5e5',
          borderRadius: 20,
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            width: `${Math.min((networkCount / 155) * 100, 100)}%`,
            height: '100%',
            background: '#107c6b'
          }}
        />
      </div>

      <div style={{ fontSize: layout.quickTitleFont }}>155</div>
      </div>
    </div>

  {/* Levely */}
<div
  style={{
    display: 'grid',
    gridTemplateColumns: 'auto auto auto 1fr auto',
    rowGap: 6,
    columnGap: 6,
    alignItems: 'center'
  }}
>
  <div>{t('LEVEL1', { level: 1 })}:</div>
  <strong>{levelCounts.l1}</strong>
  <div>·</div>
  <div>{t('stats.cashback')}:</div>
  <strong style={{ textAlign: 'right' }}>
    {Number(levelBonuses.l1 || 0).toFixed(2)} €
  </strong>

  <div>{t('LEVEL2', { level: 2 })}:</div>
  <strong>{levelCounts.l2}</strong>
  <div>·</div>
  <div>{t('stats.cashback')}:</div>
  <strong style={{ textAlign: 'right' }}>
    {Number(levelBonuses.l2 || 0).toFixed(2)} €
  </strong>

  <div>{t('LEVEL3', { level: 3 })}:</div>
  <strong>{levelCounts.l3}</strong>
  <div>·</div>
  <div>{t('stats.cashback')}:</div>
  <strong style={{ textAlign: 'right' }}>
    {Number(levelBonuses.l3 || 0).toFixed(2)} €
  </strong>
</div>
</div>

  {/* MESSAGES */}
<div
  style={{
    ...quickBox(layout),
  paddingTop: 6,
  paddingBottom: 6,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }}
  data-promo-clickable="true"
  onClick={() => setActiveModal('MESSAGES')}
  onMouseEnter={(e) => {
    e.currentTarget.style.background = '#2da9a4'
    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.35)'
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = '#f5f5f5'
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.30)'
  }}
  onMouseDown={(e) => {
    e.currentTarget.style.transform = 'scale(0.96)'
    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e) => {
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.35)'
  }}
>
<strong style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
  {t('HOME_NOTIFICATIONS')}

  {unreadWebMessagesCount > 0 && (
<img
  src={icons.bell}
  alt=""
  onMouseEnter={() => {
    const timer = setTimeout(() => {
      setShowFullscreenHint(t('HOME_UNREAD_MESSAGES_TOOLTIP'))
    }, 600)
    setTooltipTimer(timer)
  }}
  onMouseLeave={() => {
    if (tooltipTimer) clearTimeout(tooltipTimer)
    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
  style={{
    width: 22,
    height: 22,
    marginLeft: 8,
    animation: 'bellRing 1s ease-in-out infinite'
  }}
/>
  )}
</strong>

<div
  style={{
    position: 'relative',
    fontSize: layout.quickValueFont,
    fontWeight: 800,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }}
>

{/* HLAVNÉ ČÍSLO */}
<div
  key={`${webMessages.length}-${msgAnim}`}
  style={{
    position: 'relative',
    zIndex: 3,
    animation: msgAnim ? 'msgImpact 0.45s ease-out' : 'none'
  }}
>
  {notificationsData ? webMessages.length : null}
</div>

{/* ROZBITÝ TIEŇ (výbuch do strán) */}
{msgAnim && (
  <div
    style={{
      position: 'absolute',
      zIndex: 2,
      color: '#000',
      opacity: 0.35,
      animation: 'msgShadowBurst 0.45s ease-out'
    }}
  >
    {webMessages.length}
  </div>
)}

{/* OPAKUJÚCI SA PRACH/TIEŇ */}
{msgAnim && (
  <div
    style={{
      position: 'absolute',
      zIndex: 1,
      color: '#000',
      opacity: 0.2,
      animation: 'msgShadowRepeat 0.6s ease-out 7'
    }}
  >
    {webMessages.length}
  </div>
)}

</div>
  </div>
</div>
        </div>
      )}
</div>
      {activeModal === 'WALLET' && (
        <WalletModal
          open={activeModal === 'WALLET'}
          onClose={() => setActiveModal(null)}
          userId={user?.id}
          walletBalance={walletBalance}
          walletEntries={walletEntries}
        />
      )}

      {activeModal === 'SUBSCRIPTION' && (
        <SubscriptionModal
          open={true}
          onClose={() => setActiveModal(null)}
          plans={plans}
          userMembershipId={user?.membership_type_id ?? null}
          membershipValidTo={user?.membership_valid_to ?? null}
          refreshMembership={async () => {}}
        />
      )}

      <NotificationPopupModal
        open={popupOpen}
        notification={popupNotification}
        onClose={() => {
          setPopupOpen(false)
          setPopupNotification(null)
        }}
      />

      {activeModal === 'MESSAGES' && (
        <WebMessagesModal
          open={true}
          onClose={() => setActiveModal(null)}
          webMessages={webMessages}
          setWebMessages={setWebMessages}
        />
      )}

      {activeModal === 'NETWORK_OVERVIEW' && (
        <UserNetworkModal
          open={true}
          onClose={() => setActiveModal(null)}
        />
      )}

{activeModal === 'APP_BENEFITS' && (
  <AppFeaturesModalHome
    open={true}
    onClose={() => setActiveModal(null)}
  />
)}

{activeModal === 'HOW_IT_WORKS' && (
  <HowWorks
    open={true}
    onClose={() => setActiveModal(null)}
  />
)}

{activeModal === 'USER_PROFILE' && (
  <PersonalDataModal
    open={true}
    onClose={() => setActiveModal(null)}
  />
)}

{activeModal === 'STATS_BUTTON' && (
//  <ComingSoonModal
//    open={true}
//    onClose={() => setActiveModal(null)}
  <StatsModal
    open={true}
    onClose={() => setActiveModal(null)}
    userId={user?.id}
  />
)}

{activeModal === 'ADDS' && (
  <AdvertisementModal
    open={true}
    onClose={() => setActiveModal(null)}
  />
)}

{activeModal === 'QR' && (
  <QRModal
    open={true}
    referralCode={user?.referral_code}
    isReferralMaxed={levelCounts.l1 >= 5}
    hoverBtn={hoverBtn}
    pressedBtn={pressedBtn}
    setHoverBtn={setHoverBtn}
    setPressedBtn={setPressedBtn}
    onClose={() => setActiveModal(null)}
    t={t}
  />
)}
      {paymentSuccessOpen && (
        <PaymentSuccessModal
          open={true}
          onClose={() => setPaymentSuccessOpen(false)}
        />
      )}

<MFAPromptModal
  open={showMFAPrompt}
  onClose={() => setShowMFAPrompt(false)}
  onEnable={() => {
    setShowMFAPrompt(false)
    setShowMFAModal(true)
  }}
/>

<MFAModal
  open={showMFAModal}
  enabled={!!user?.mfa_enabled}
  onClose={() => setShowMFAModal(false)}
  onStatusChange={async () => {
    await refreshUser(true)
  }}
/>

{showReferralHint && (
  <div
    style={{
      position: 'fixed',
      top: referralCursorPos.y -50,
      left: referralCursorPos.x - 60,
      transform: 'translateX(-50%)',
      background: 'rgba(0,0,0,0.85)',
      color: '#3BC6C4',
      padding: '6px 14px 8px 14px',
      borderRadius: 12,
      fontSize: 16,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      zIndex: 5000,
      border: '1.5px solid #107c6b',
      boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
      display: 'flex',
      alignItems: 'center',
      lineHeight: 1
    }}
  >
    {t('REFERRAL_CODE')}
  </div>
)}
{showFullscreenHint && (
  <div
    style={{
      position: 'fixed',
      top: cursorPos.y - 50,
      left: cursorPos.x,
      transform: 'translateX(-50%)',
      background: 'rgba(0,0,0,0.85)',
      color: '#3BC6C4',
      padding: '6px 14px 8px 14px',
      borderRadius: 12,
      fontSize: 16,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      zIndex: 5000,
      border: '1.5px solid #107c6b',
      boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
      display: 'flex',
      alignItems: 'center',
      lineHeight: 1
    }}
  >
    {typeof showFullscreenHint === 'string'
      ? showFullscreenHint
      : t('MODAL_DETAILS')}
  </div>
)}

    </div>
  )
}

export default Home

const wrapper = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column'
}

const contentArea = {
  flex: 1,
  display: 'grid',
  minHeight: 0
}

const defaultCenterStack = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  height: '100%',
  minHeight: 0
}

const leftColumn = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0
}

const boxList = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6
}

const box = {
  background: 'rgba(16,124,107,0.06)',
  border: '1.5px solid #107c6b',
  borderRadius: 10,
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: '0 4px 4px rgba(0,0,0,0.20)',
  transition: 'transform 0.05s ease, box-shadow 0.1s ease'
}

const boxContent = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  width: '100%'
}

const getBoxIcon = (layout) => ({
  width: layout.iconSize,
  height: layout.iconSize,
  objectFit: 'contain'
})

const boxTitle = {
  margin: 0,
  color: '#0f3f39',
  textAlign: 'center'
}

const bottomBox = {
  background: '#f5f5f5',
  border: '1.5px solid #107c6b',
  borderRadius: 10,
  padding: 16,
  textAlign: 'center',
  cursor: 'pointer',
  flex: 1,
  minHeight: 0,
  overflow: 'auto'
}

const lastTripBox = {
  background: '#ededed',
  border: '1.5px solid #107c6b',
  borderRadius: 10,
  padding: 12,
  textAlign: 'left',
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column'
}

const lastTripTableWrap = {
  border: '1px solid #c7c7c7',
  borderRadius: 10,
  overflow: 'hidden',
  background: '#fff',
  flex: 1,
  minHeight: 0
}

const lastTripHeaderCell = {
  textAlign: 'left',
  padding: '8px 12px',
  fontWeight: 700,
fontSize: 13
}

const lastTripBodyCell = {
  textAlign: 'left',
  padding: '10px 12px',
  borderTop: '1px solid #ececec',
fontSize: 13
}

const rightColumn = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0
}

const rightPanel = {
  background: 'rgba(16,124,107,0.06)',
  border: '1.5px solid #107c6b',
  borderRadius: 10,
  padding: '12px 12px 4px 12px'
}

const quickBox = (layout) => ({
  background: '#f5f5f5',
  padding: layout.quickPadding,
  borderRadius: 8,
  marginBottom: 12,
  border: '1.5px solid #107c6b',
  boxShadow: '0 4px 8px rgba(0,0,0,0.30)',
  transition: 'transform 0.05s ease, box-shadow 0.1s ease',
  cursor: 'pointer'
})

const userBox = {
  marginBottom: 12,
  padding: '6px 12px',
  background: 'rgba(16,124,107,0.08)',
  border: '1.5px solid #107c6b',
  borderRadius: 10
}

const userLabel = {
  fontSize: 16,
  fontWeight: 600,
  color: '#000',
  marginTop: -2,
}

const userName = {
  fontSize: 20,
  fontWeight: 700,
  color: '#000'
}

const bellStyle = document.createElement('style')
bellStyle.innerHTML = `
@keyframes bellRing {
  0% { transform: rotate(0deg); }
  10% { transform: rotate(12deg); }
  20% { transform: rotate(-10deg); }
  30% { transform: rotate(8deg); }
  40% { transform: rotate(-6deg); }
  50% { transform: rotate(4deg); }
  60% { transform: rotate(-2deg); }
  70% { transform: rotate(1deg); }
  80% { transform: rotate(-1deg); }
  100% { transform: rotate(0deg); }
}
`
document.head.appendChild(bellStyle)

const msgAnimStyle = document.createElement('style')
msgAnimStyle.innerHTML = `

@keyframes msgImpact {
  0% {
    transform: scale(2.5);
    opacity: 0;
  }
  60% {
    transform: scale(1);
    opacity: 1;
  }
  75% {
    transform: scale(0.85);
  }
  85% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes msgShadowBurst {
  0% {
    transform: scale(1);
    opacity: 0.4;
  }
  100% {
    transform: scale(4) rotate(2deg);
    opacity: 0;
  }
}

@keyframes msgShadowRepeat {
  0% {
    transform: scale(1);
    opacity: 0.25;
  }
  30% {
    transform: scale(2.5);
    opacity: 0.1;
  }
  60% {
    transform: scale(1.6);
    opacity: 0.2;
  }
  100% {
    transform: scale(1);
    opacity: 0.25;
  }
}

`
document.head.appendChild(msgAnimStyle)